/**
 * Analytics API Service
 * Connects frontend to Phase 6.6 Backend Analytics & AI Management APIs
 */

import { apiClient } from './aiCrmApi';

// ==================== TYPES ====================

export interface AnalyticsSummary {
  conversionRate: number;
  conversionRateTrend: number;
  monthlyRevenue: number;
  revenueTrend: number;
  activeSalesReps: number;
  activeRepsChange: number;
  avgDealCycle: number;
  dealCycleTrend: number;
}

export interface SalesRepPerformance {
  salesRepId: string;
  name: string;
  revenue: number;
  conversions: number;
  conversionRate: number;
}

export interface FunnelStage {
  stage: string;
  count: number;
  percentage: number;
}

export interface TrendDataPoint {
  month: string;
  newLeads: number;
  conversions: number;
  conversionRate: number;
}

export interface AIInsight {
  id: string;
  category: 'performance' | 'opportunity' | 'risk' | 'recommendation';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  metric?: string;
  metricValue?: string;
  trend?: 'up' | 'down';
  actionable?: boolean;
  suggestedAction?: string;
}

export interface TaskSummary {
  total: number;
  completed: number;
  pending: number;
  overdue: number;
  completionRate: number;
}

export interface UpcomingTask {
  id: string;
  title: string;
  assignee: string;
  dueDate: string;
  priority: string;
  status: string;
  leadName?: string;
}

export interface TasksData {
  summary: TaskSummary;
  upcomingTasks: UpcomingTask[];
}

// AI Management Types
export interface SalesRepPerformanceData {
  id: string;
  name: string;
  avatar: string | null;
  leadsAssigned: number;
  leadsConverted: number;
  conversionRate: number;
  revenue: number;
  avgDealSize: number;
  avgDealCycle: number;
  activitiesCompleted: number;
  tasksOverdue: number;
  responseTime: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
}

export interface CoachingRecommendation {
  id: string;
  salesRepId: string;
  salesRepName: string;
  area: 'conversion' | 'response_time' | 'deal_cycle' | 'activity' | 'communication';
  priority: 'high' | 'medium' | 'low';
  currentScore: number;
  targetScore: number;
  strengths: string[];
  weaknesses: string[];
  actionItems: string[];
  estimatedImpact: string;
  timeframe: string;
}

export interface LeadMatch {
  salesRepId: string;
  salesRepName: string;
  matchScore: number;
  reasons: string[];
  availability: 'available' | 'busy' | 'full';
  currentLeads: number;
  maxCapacity: number;
}

export interface LeadAssignmentSuggestion {
  leadId: string;
  leadName: string;
  leadIndustry: string;
  leadValue: number;
  aiScore: number;
  topMatches: LeadMatch[];
}

export interface PerformancePrediction {
  metric: string;
  current: number;
  predicted: number;
  confidence: number;
  trend: 'up' | 'down' | 'stable';
  changePercentage: number;
  unit?: string;
}

export interface PerformanceGoal {
  id: string;
  name: string;
  target: number;
  current: number;
  predicted: number;
  deadline: string;
  status: 'on_track' | 'at_risk' | 'behind';
  aiRecommendation: string;
}

export interface PredictionsData {
  predictions: PerformancePrediction[];
  goals: PerformanceGoal[];
}

// ==================== ANALYTICS APIs ====================

export const analyticsApi = {
  /**
   * GET /api/v1/ai-crm/analytics/summary
   * Get dashboard summary metrics
   */
  getSummary: async (): Promise<AnalyticsSummary> => {
    const response = await apiClient.get('/analytics/summary');
    return response.data.data;
  },

  /**
   * GET /api/v1/ai-crm/analytics/sales-ranking
   * Get sales rep performance ranking
   */
  getSalesRanking: async (): Promise<SalesRepPerformance[]> => {
    const response = await apiClient.get('/analytics/sales-ranking');
    return response.data.data;
  },

  /**
   * GET /api/v1/ai-crm/analytics/funnel
   * Get conversion funnel data
   */
  getFunnel: async (): Promise<FunnelStage[]> => {
    const response = await apiClient.get('/analytics/funnel');
    return response.data.data;
  },

  /**
   * GET /api/v1/ai-crm/analytics/trends
   * Get conversion trends (last 12 months)
   */
  getTrends: async (): Promise<TrendDataPoint[]> => {
    const response = await apiClient.get('/analytics/trends');
    return response.data.data;
  },

  /**
   * GET /api/v1/ai-crm/analytics/insights
   * Get AI-generated insights
   */
  getInsights: async (): Promise<AIInsight[]> => {
    const response = await apiClient.get('/analytics/insights');
    return response.data.data;
  },

  /**
   * GET /api/v1/ai-crm/analytics/tasks
   * Get task tracking summary
   */
  getTasks: async (): Promise<TasksData> => {
    const response = await apiClient.get('/analytics/tasks');
    return response.data.data;
  },
};

// ==================== AI MANAGEMENT APIs ====================

export const aiManagementApi = {
  /**
   * GET /api/v1/ai-crm/ai-management/performance-table
   * Get detailed performance data for all sales reps
   */
  getPerformanceTable: async (): Promise<SalesRepPerformanceData[]> => {
    const response = await apiClient.get('/ai-management/performance-table');
    return response.data.data;
  },

  /**
   * GET /api/v1/ai-crm/ai-management/coaching-recommendations
   * Get AI-generated coaching recommendations
   */
  getCoachingRecommendations: async (): Promise<CoachingRecommendation[]> => {
    const response = await apiClient.get('/ai-management/coaching-recommendations');
    return response.data.data;
  },

  /**
   * GET /api/v1/ai-crm/ai-management/lead-assignments
   * Get AI-powered lead assignment suggestions
   */
  getLeadAssignments: async (): Promise<LeadAssignmentSuggestion[]> => {
    const response = await apiClient.get('/ai-management/lead-assignments');
    return response.data.data;
  },

  /**
   * POST /api/v1/ai-crm/ai-management/assign-lead
   * Assign a lead to a sales rep
   */
  assignLead: async (data: { leadId: string; salesRepId: string }): Promise<any> => {
    const response = await apiClient.post('/ai-management/assign-lead', data);
    return response.data.data;
  },

  /**
   * GET /api/v1/ai-crm/ai-management/predictions
   * Get AI-powered performance predictions
   */
  getPredictions: async (): Promise<PredictionsData> => {
    const response = await apiClient.get('/ai-management/predictions');
    return response.data.data;
  },
};

export default {
  analytics: analyticsApi,
  aiManagement: aiManagementApi,
};
